import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, catchError, tap, map } from 'rxjs';
import { ConversationSummary, ConversationListResponse } from '../../models/conversation-summary';
import { LoggingService } from '../logging/logging.service';

/**
 * State for the conversation list (FIN-0065 compliant - NO activeConversationId here!)
 * Active conversation is managed by ConversationManager.currentConversationId$
 */
export interface ConversationListState {
  conversations: ConversationSummary[];
  loading: boolean;
  hasMore: boolean;
  currentPage: number;
  totalElements: number;
  error: string | null;
}

const DEFAULT_PAGE_SIZE = 20;

/**
 * Service for managing the conversation list in the chat history sidebar.
 * FIN-0102: Chat History Feature
 *
 * NOTE: This service manages ONLY the list of conversations.
 * The active conversation ID is managed by ConversationManager (FIN-0065 compliance).
 */
@Injectable({
  providedIn: 'root'
})
export class ConversationListService {
  private stateSubject = new BehaviorSubject<ConversationListState>({
    conversations: [],
    loading: false,
    hasMore: true,
    currentPage: 0,
    totalElements: 0,
    error: null
  });

  public state$ = this.stateSubject.asObservable();

  constructor(
    private http: HttpClient,
    private logger: LoggingService
  ) {}

  /**
   * Load the first page of conversations.
   * Call this when opening the sidebar.
   */
  loadConversations(): void {
    this.logger.info('ConversationListService', 'Loading first page of conversations');

    this.stateSubject.next({
      ...this.stateSubject.value,
      loading: true,
      error: null
    });

    this.fetchConversations(0).subscribe({
      next: (response) => {
        this.stateSubject.next({
          conversations: response.content,
          loading: false,
          hasMore: response.number < response.totalPages - 1,
          currentPage: response.number,
          totalElements: response.totalElements,
          error: null
        });
        this.logger.info('ConversationListService', `Loaded ${response.content.length} conversations (total: ${response.totalElements})`);
      },
      error: (error) => {
        this.logger.error('ConversationListService', 'Failed to load conversations', error);
        this.stateSubject.next({
          ...this.stateSubject.value,
          loading: false,
          error: 'Failed to load conversations'
        });
      }
    });
  }

  /**
   * Load the next page of conversations (infinite scroll).
   */
  loadMore(): void {
    const currentState = this.stateSubject.value;

    if (currentState.loading || !currentState.hasMore) {
      return;
    }

    const nextPage = currentState.currentPage + 1;
    this.logger.info('ConversationListService', `Loading page ${nextPage}`);

    this.stateSubject.next({
      ...currentState,
      loading: true
    });

    this.fetchConversations(nextPage).subscribe({
      next: (response) => {
        this.stateSubject.next({
          conversations: [...currentState.conversations, ...response.content],
          loading: false,
          hasMore: response.number < response.totalPages - 1,
          currentPage: response.number,
          totalElements: response.totalElements,
          error: null
        });
        this.logger.info('ConversationListService', `Loaded ${response.content.length} more conversations`);
      },
      error: (error) => {
        this.logger.error('ConversationListService', 'Failed to load more conversations', error);
        this.stateSubject.next({
          ...currentState,
          loading: false,
          error: 'Failed to load more conversations'
        });
      }
    });
  }

  /**
   * Refresh the conversation list (pull-to-refresh or after creating new conversation).
   */
  refresh(): void {
    this.loadConversations();
  }

  /**
   * Update conversation title (rename).
   */
  updateTitle(conversationId: string, newTitle: string): Observable<ConversationSummary> {
    this.logger.info('ConversationListService', `Updating title for ${conversationId}`);

    return this.http.patch<ConversationSummary>(
      `/api/conversations/${conversationId}`,
      { title: newTitle }
    ).pipe(
      tap((updated) => {
        // Update local state optimistically
        const currentState = this.stateSubject.value;
        const updatedConversations = currentState.conversations.map(conv =>
          conv.id === conversationId ? { ...conv, title: updated.title } : conv
        );
        this.stateSubject.next({
          ...currentState,
          conversations: updatedConversations
        });
        this.logger.info('ConversationListService', `Title updated for ${conversationId}`);
      }),
      catchError((error) => {
        this.logger.error('ConversationListService', `Failed to update title for ${conversationId}`, error);
        throw error;
      })
    );
  }

  /**
   * Delete a conversation (soft delete).
   */
  deleteConversation(conversationId: string): Observable<void> {
    this.logger.info('ConversationListService', `Deleting conversation ${conversationId}`);

    return this.http.delete<void>(`/api/conversations/${conversationId}`).pipe(
      tap(() => {
        // Remove from local state
        const currentState = this.stateSubject.value;
        const updatedConversations = currentState.conversations.filter(
          conv => conv.id !== conversationId
        );
        this.stateSubject.next({
          ...currentState,
          conversations: updatedConversations,
          totalElements: currentState.totalElements - 1
        });
        this.logger.info('ConversationListService', `Conversation ${conversationId} deleted`);
      }),
      catchError((error) => {
        this.logger.error('ConversationListService', `Failed to delete conversation ${conversationId}`, error);
        throw error;
      })
    );
  }

  /**
   * Add a new conversation to the top of the list.
   * Called after creating a new conversation.
   */
  prependConversation(conversation: ConversationSummary): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      conversations: [conversation, ...currentState.conversations],
      totalElements: currentState.totalElements + 1
    });
  }

  /**
   * Update a conversation in the list (e.g., after title is auto-generated).
   */
  updateConversation(conversation: ConversationSummary): void {
    const currentState = this.stateSubject.value;
    const index = currentState.conversations.findIndex(c => c.id === conversation.id);

    if (index >= 0) {
      const updatedConversations = [...currentState.conversations];
      updatedConversations[index] = conversation;
      this.stateSubject.next({
        ...currentState,
        conversations: updatedConversations
      });
    }
  }

  /**
   * Fetch conversations from the API.
   */
  private fetchConversations(page: number): Observable<ConversationListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', DEFAULT_PAGE_SIZE.toString());

    return this.http.get<ConversationListResponse>('/api/conversations', { params });
  }
}
